import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  useTheme,
  alpha,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Badge
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pause as PauseIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  ReportProblem as ReportProblemIcon,
  WhatsApp as WhatsAppIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { ftthCaixaService, FtthCaixa } from '../../services/ftthCaixaService';
import CaixaForm from '../../components/forms/FtthCaixaCtoForm';
import FtthCaixaCeoForm from '../../components/forms/FtthCaixaCeoForm';
import SysKpiCard from '../../components/common/SysKpiCard';
import { getCaixaStatusColor, getCaixaStatusIcon, getCaixaStatusLabel } from '../../utils/caixaStatusUtils';
import { MonitorStatusChip } from '../../utils/monitorDisplayUtils';
import EscritorioSelect from '../../components/selects/EscritorioSelect';
import { GatewayWhatsappSelect } from '../../components/selects/GatewayWhatsappSelect';
import { SysEditorWhatsapp } from '../../components/syseditor';
import mensagemService from '../../services/mensagemService';
import { contactService } from '../../services/contactService';
import { Contact } from '../../types';
import { enqueueSnackbar } from 'notistack';

type StatusFilter = 'all' | 'ativo' | 'inativo' | 'aguardando_instalacao' | 'manutencao' | 'online' | 'offline';

type TipoFilter = 'all' | 'cto' | 'ceo';

function getStatusQueryParams(statusFilter: StatusFilter) {
  if (statusFilter === 'online' || statusFilter === 'offline') {
    return {
      status: 'ativo',
      monitor_status: statusFilter,
    };
  }

  return {
    status: statusFilter === 'all' ? undefined : statusFilter,
  };
}

type CaixaSortField = 'status' | 'portas';

type CardFilter = 'online' | 'offline' | 'aguardando_instalacao' | 'ativas' | 'zero_portas_livres';

function matchesCardFilter(caixa: FtthCaixa, filter: CardFilter): boolean {
  if (caixa.tipo !== 'cto') return false;

  switch (filter) {
    case 'online':
      return caixa.status === 'ativo' && getCaixaMonitorStatus(caixa) === 'online';
    case 'offline':
      return caixa.status === 'ativo' && getCaixaMonitorStatus(caixa) === 'offline';
    case 'aguardando_instalacao':
      return caixa.status === 'aguardando_instalacao';
    case 'ativas':
      return caixa.status === 'ativo';
    case 'zero_portas_livres':
      return getPortasLivres(caixa) === 0;
    default:
      return true;
  }
}

const cardFilterLabels: Record<CardFilter, string> = {
  online: 'CTO Online',
  offline: 'CTO Offline',
  aguardando_instalacao: 'Aguardando Instalação',
  ativas: 'CTO Ativas',
  zero_portas_livres: '0 Portas Livres',
};

const offlineChipBlinkSx = {
  animation: 'caixaOfflineBlink 1.2s ease-in-out infinite',
  '@keyframes caixaOfflineBlink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.35 },
  },
} as const;

const zeroPortDotBlinkSx = {
  display: 'inline-block',
  width: 8,
  height: 8,
  borderRadius: '50%',
  bgcolor: 'error.main',
  ml: 0.5,
  verticalAlign: 'middle',
  animation: 'portaLivreZeroBlink 1s ease-in-out infinite',
  '@keyframes portaLivreZeroBlink': {
    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
    '50%': { opacity: 0.35, transform: 'scale(0.85)' },
  },
} as const;

function getCaixaMonitorStatus(caixa: FtthCaixa): 'online' | 'offline' | 'vazia' {
  return (caixa.monitor_status ?? caixa.funcionamento ?? 'vazia') as 'online' | 'offline' | 'vazia';
}

function getPonPortSfp(caixa: FtthCaixa): string | null {
  return caixa.sfp ?? caixa.pon_port?.sfp ?? null;
}

function hasPonVinculo(caixa: FtthCaixa): boolean {
  return !!(caixa.pon_port || caixa.port_id);
}

function getDisabledPortsCount(caixa: FtthCaixa): number {
  const raw = caixa.disabled_ports as FtthCaixa['disabled_ports'] | string | null | undefined;

  if (Array.isArray(raw)) {
    return raw.length;
  }

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      return 0;
    }
  }

  const countFromQuery = Number((caixa as FtthCaixa & { portas_desabilitadas_count?: number }).portas_desabilitadas_count);
  return Number.isFinite(countFromQuery) ? countFromQuery : 0;
}

function getPortasLivres(caixa: FtthCaixa): number {
  if (caixa.tipo !== 'cto' || caixa.capacidade == null) {
    return -1;
  }

  const emUso = Number(caixa.portas_em_uso ?? 0);
  const desabilitadas = getDisabledPortsCount(caixa);
  return Math.max(caixa.capacidade - emUso - desabilitadas, 0);
}

function getCaixaStatusSortRank(caixa: FtthCaixa): number {
  if (caixa.status === 'ativo') {
    const monitor = getCaixaMonitorStatus(caixa);
    if (monitor === 'offline') return 0;
    if (monitor === 'vazia') return 1;
    return 2;
  }

  const statusRank: Record<string, number> = {
    inativo: 10,
    aguardando_instalacao: 11,
    manutencao: 12,
  };

  return statusRank[caixa.status] ?? 99;
}

function formatPortasCaixa(caixa: FtthCaixa): React.ReactNode {
  if (caixa.tipo !== 'cto' || caixa.capacidade == null) {
    return '—';
  }

  const emUso = Number(caixa.portas_em_uso ?? 0);
  const livres = getPortasLivres(caixa);

  return (
    <span>
      Total: {caixa.capacidade} · Em uso: {emUso} · Livres: <b>{livres}</b>
      {livres === 0 && <Box component="span" sx={zeroPortDotBlinkSx} />}
    </span>
  );
}

type CaixasSummary = {
  online: number;
  offline: number;
  aguardandoInstalacao: number;
  ativas: number;
  zeroPortasLivres: number;
  totalCaixas: number;
  totalCto: number;
  totalCeo: number;
};

const emptySummary: CaixasSummary = {
  online: 0,
  offline: 0,
  aguardandoInstalacao: 0,
  ativas: 0,
  zeroPortasLivres: 0,
  totalCaixas: 0,
  totalCto: 0,
  totalCeo: 0,
};

const MANAGED_CAIXA_TIPOS = ['cto', 'ceo'] as const;

function normalizeCaixaTipo(tipo: string | null | undefined): string {
  return (tipo ?? '').toLowerCase();
}

function isManagedCaixaTipo(tipo: string | null | undefined): boolean {
  return MANAGED_CAIXA_TIPOS.includes(normalizeCaixaTipo(tipo) as typeof MANAGED_CAIXA_TIPOS[number]);
}

function filterManagedCaixas(caixas: FtthCaixa[]): FtthCaixa[] {
  return caixas.filter((caixa) => isManagedCaixaTipo(caixa.tipo));
}

function formatTotalCaixasSubtitle(summary: CaixasSummary): string {
  return `CTO: ${summary.totalCto} · CEO: ${summary.totalCeo}`;
}

function computeCaixasSummary(caixas: FtthCaixa[]): CaixasSummary {
  const managedCaixas = filterManagedCaixas(caixas);
  const ctos = managedCaixas.filter((caixa) => normalizeCaixaTipo(caixa.tipo) === 'cto');
  const ceos = managedCaixas.filter((caixa) => normalizeCaixaTipo(caixa.tipo) === 'ceo');
  const totalCto = ctos.length;
  const totalCeo = ceos.length;

  return {
    online: ctos.filter((caixa) => caixa.status === 'ativo' && getCaixaMonitorStatus(caixa) === 'online').length,
    offline: ctos.filter((caixa) => caixa.status === 'ativo' && getCaixaMonitorStatus(caixa) === 'offline').length,
    aguardandoInstalacao: ctos.filter((caixa) => caixa.status === 'aguardando_instalacao').length,
    ativas: ctos.filter((caixa) => caixa.status === 'ativo').length,
    zeroPortasLivres: ctos.filter((caixa) => getPortasLivres(caixa) === 0).length,
    totalCaixas: totalCto + totalCeo,
    totalCto,
    totalCeo,
  };
}

interface CaixasTabProps {
  createFormRequest?: 'cto' | 'ceo' | null;
  onCreateFormRequestHandled?: () => void;
  notifyTrigger?: number;
  onSelectionChange?: (count: number) => void;
}

const CaixasTab: React.FC<CaixasTabProps> = ({
  createFormRequest = null,
  onCreateFormRequestHandled,
  notifyTrigger,
  onSelectionChange,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [caixas, setCaixas] = useState<FtthCaixa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [portsFilter, setPortsFilter] = useState('');
  const [tipoFilter, setTipoFilter] = useState<TipoFilter>('cto'); // CTO selecionado por padrão
  const [escritorioFilter, setEscritorioFilter] = useState<number | null>(null);
  const [orderBy, setOrderBy] = useState<CaixaSortField | null>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [summary, setSummary] = useState<CaixasSummary>(emptySummary);
  const [summaryCaixas, setSummaryCaixas] = useState<FtthCaixa[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [cardFilter, setCardFilter] = useState<CardFilter | null>(null);

  // Estados para seleção e notificação em massa
  const [selectedCaixaIds, setSelectedCaixaIds] = useState<Set<number>>(new Set());
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [notifyMensagem, setNotifyMensagem] = useState('');
  const [notifyGateway, setNotifyGateway] = useState<number | ''>(9999);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyResults, setNotifyResults] = useState<{ caixaName: string; clientName: string; success: boolean; error?: string }[]>([]);
  const [notifyStep, setNotifyStep] = useState<'form' | 'sending' | 'results'>('form');
  const [notifyProgress, setNotifyProgress] = useState({ current: 0, total: 0, currentCaixaName: '' });



  // Modal para formulário CAIXA
  const [caixaFormDialog, setCaixaFormDialog] = useState<{
    open: boolean;
    caixa: FtthCaixa | null; // Revertido: volta a enviar objeto completo
    tipo?: 'cto' | 'ceo';
  }>({
    open: false,
    caixa: null
  });

  useEffect(() => {
    loadCaixas();
  }, [page, rowsPerPage, searchTerm, statusFilter, portsFilter, tipoFilter, escritorioFilter]);

  const getSummaryQueryParams = () => ({
    search: searchTerm || undefined,
    ...getStatusQueryParams(statusFilter),
    capacidade: portsFilter ? parseInt(portsFilter) : undefined,
    escritorio_id: escritorioFilter || undefined,
    tipos: 'cto,ceo',
  });

  const getTableQueryParams = () => ({
    search: searchTerm || undefined,
    ...getStatusQueryParams(statusFilter),
    capacidade: portsFilter ? parseInt(portsFilter) : undefined,
    escritorio_id: escritorioFilter || undefined,
    ...(tipoFilter === 'all' ? { tipos: 'cto,ceo' } : { tipo: tipoFilter }),
  });

  const loadSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);

      const response = await ftthCaixaService.getAll({
        page: 1,
        limit: 10000,
        ...getSummaryQueryParams(),
      });

      const payload = response?.data ?? response;
      const caixasList = payload?.caixas ?? [];

      if (Array.isArray(caixasList)) {
        const managedCaixas = filterManagedCaixas(caixasList);
        setSummaryCaixas(managedCaixas);
        setSummary(computeCaixasSummary(managedCaixas));
      } else {
        setSummaryCaixas([]);
        setSummary(emptySummary);
      }
    } catch (err) {
      console.error('Erro ao carregar resumo de caixas:', err);
      setSummary(emptySummary);
    } finally {
      setSummaryLoading(false);
    }
  }, [searchTerm, statusFilter, portsFilter, escritorioFilter]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const loadCaixas = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ftthCaixaService.getAll({
        page: page + 1,
        limit: rowsPerPage,
        ...getTableQueryParams(),
      });

      const payload = response?.data ?? response;
      const caixasList = filterManagedCaixas(payload?.caixas ?? []);
      const pagination = payload?.pagination ?? response?.pagination;

      if (Array.isArray(caixasList)) {
        setCaixas(caixasList);
        setTotalCount(pagination?.total ?? caixasList.length);
      } else {
        throw new Error('Erro ao carregar CAIXAs');
      }

    } catch (err: any) {
      console.error('Erro ao carregar CAIXAs:', err);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (caixa: FtthCaixa) => {
    try {
      // Toggle entre ativo e inativo
      const newStatus = caixa.status === 'ativo' ? 'inativo' : 'ativo';
      await ftthCaixaService.update(caixa.id, { status: newStatus });
      loadCaixas();
      loadSummary();
    } catch (err: any) {
      console.error('Erro ao alterar status:', err);
      setError(err.message || 'Erro ao alterar status');
    }
  };



  const handleOpenCaixaForm = (caixa?: FtthCaixa, tipo?: 'cto' | 'ceo') => {
    const caixaTipo = caixa?.tipo;
    const resolvedTipo = caixa
      ? (isManagedCaixaTipo(caixaTipo) ? normalizeCaixaTipo(caixaTipo) as 'cto' | 'ceo' : undefined)
      : tipo;

    setCaixaFormDialog({
      open: true,
      caixa: caixa || null,
      tipo: resolvedTipo,
    });
  };

  useEffect(() => {
    if (!createFormRequest) return;
    handleOpenCaixaForm(undefined, createFormRequest);
    onCreateFormRequestHandled?.();
  }, [createFormRequest, onCreateFormRequestHandled]);

  const handleCloseCaixaForm = () => {
    setCaixaFormDialog({
      open: false,
      caixa: null,
      tipo: undefined
    });
  };

  const handleCaixaFormSuccess = (caixa: FtthCaixa) => {
    handleCloseCaixaForm();
    loadCaixas();
    loadSummary();
  };

  const handleRefresh = () => {
    loadCaixas();
    loadSummary();
  };

  // === Seleção de caixas e notificação em massa ===
  const handleToggleSelectCaixa = (caixaId: number) => {
    setSelectedCaixaIds((prev) => {
      const next = new Set(prev);
      if (next.has(caixaId)) {
        next.delete(caixaId);
      } else {
        next.add(caixaId);
      }
      return next;
    });
  };


  const handleOpenNotifyModal = () => {
    setNotifyMensagem('');
    setNotifyGateway(9999);
    setNotifyResults([]);
    setNotifyStep('form');
    setNotifyProgress({ current: 0, total: 0, currentCaixaName: '' });
    setNotifyModalOpen(true);
  };

  const handleCloseNotifyModal = () => {
    if (!notifyLoading) {
      setNotifyModalOpen(false);
    }
  };

  const handleSendBulkNotifications = async () => {
    if (!notifyMensagem.trim()) {
      enqueueSnackbar('Digite uma mensagem para enviar', { variant: 'warning' });
      return;
    }
    if (!notifyGateway) {
      enqueueSnackbar('Selecione um gateway WhatsApp', { variant: 'warning' });
      return;
    }
    if (selectedCaixasList.length === 0) {
      enqueueSnackbar('Nenhuma caixa selecionada', { variant: 'warning' });
      return;
    }

    setNotifyLoading(true);
    setNotifyStep('sending');
    const results: { caixaName: string; clientName: string; success: boolean; error?: string }[] = [];
    const processedClientIds = new Set<number>(); // Deduplicação global

    setNotifyProgress({ current: 0, total: selectedCaixasList.length, currentCaixaName: '' });

    for (let i = 0; i < selectedCaixasList.length; i++) {
      const caixa = selectedCaixasList[i];
      setNotifyProgress({ current: i + 1, total: selectedCaixasList.length, currentCaixaName: caixa.name });

      try {
        // Buscar clientes da caixa
        const response = await ftthCaixaService.getClientsByCto(caixa.id);
        if (!response.success || !response.data) {
          results.push({ caixaName: caixa.name, clientName: '—', success: false, error: 'Erro ao buscar clientes' });
          continue;
        }

        const occupiedPorts = response.data.ports.filter(
          (p) => p.status === 'occupied' && p.info?.client_id
        );

        // Deduplica clientes dentro da caixa
        const clientMap = new Map<number, { clientId: number; clientName: string }>();
        occupiedPorts.forEach((port) => {
          const clientId = (port.info as any)?.client_id;
          const clientName = (port.info as any)?.client_name || `Cliente #${clientId}`;
          if (clientId && !clientMap.has(clientId)) {
            clientMap.set(clientId, { clientId, clientName });
          }
        });

        if (clientMap.size === 0) {
          results.push({ caixaName: caixa.name, clientName: '—', success: false, error: 'Nenhum cliente encontrado' });
          continue;
        }

        for (const client of clientMap.values()) {
          // Deduplicação global entre caixas
          if (processedClientIds.has(client.clientId)) {
            results.push({ caixaName: caixa.name, clientName: client.clientName, success: true, error: 'Já notificado (outra caixa)' });
            continue;
          }
          processedClientIds.add(client.clientId);

          try {
            const contactsResponse = await contactService.getByClientId(client.clientId, {
              limit: 100,
              filters: { is_whatsapp: true, tipo: 'celular', recebe_notificacao: true }
            });

            const whatsappContacts = (contactsResponse.data || []).filter(
              (c: Contact) => c.is_whatsapp && c.tipo === 'celular' && c.recebe_notificacao
            );

            if (whatsappContacts.length === 0) {
              results.push({ caixaName: caixa.name, clientName: client.clientName, success: false, error: 'Sem contato WhatsApp ativo' });
              continue;
            }

            for (const contact of whatsappContacts) {
              try {
                await mensagemService.createMensagem({
                  tipo: 'whatsapp',
                  destino: contact.id,
                  conteudo: notifyMensagem.trim(),
                  cliente_id: client.clientId,
                  gateway_id: notifyGateway === 9999 ? undefined : Number(notifyGateway)
                });
                results.push({ caixaName: caixa.name, clientName: `${client.clientName} (${contact.nome || contact.numero})`, success: true });
              } catch (err: any) {
                results.push({ caixaName: caixa.name, clientName: `${client.clientName} (${contact.nome || contact.numero})`, success: false, error: err.message || 'Erro ao cadastrar' });
              }
            }
          } catch (err: any) {
            results.push({ caixaName: caixa.name, clientName: client.clientName, success: false, error: err.message || 'Erro ao buscar contatos' });
          }
        }
      } catch (err: any) {
        results.push({ caixaName: caixa.name, clientName: '—', success: false, error: err.message || 'Erro ao processar caixa' });
      }
    }

    setNotifyResults(results);
    setNotifyStep('results');
    setNotifyLoading(false);

    const successCount = results.filter((r) => r.success && !r.error).length;
    const skipCount = results.filter((r) => r.success && r.error).length;
    const failCount = results.filter((r) => !r.success).length;

    if (successCount > 0 && failCount === 0) {
      enqueueSnackbar(`${successCount} mensagem(ns) cadastrada(s)${skipCount > 0 ? `, ${skipCount} já notificado(s)` : ''}`, { variant: 'success' });
    } else if (successCount > 0) {
      enqueueSnackbar(`${successCount} cadastrada(s), ${failCount} falha(s)`, { variant: 'warning' });
    } else {
      enqueueSnackbar('Falha ao cadastrar mensagens', { variant: 'error' });
    }

    setSelectedCaixaIds(new Set());
  };

  const handleViewDetails = (caixa: FtthCaixa) => {
    navigate(`/ftth/caixas/${caixa.id}`);
  };

  const handleRequestSort = (field: CaixaSortField) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
  };

  const handleCardFilterClick = (filter: CardFilter) => {
    setPage(0);
    setCardFilter((current) => (current === filter ? null : filter));
    setTipoFilter('cto');
  };

  const clearCardFilter = () => {
    setCardFilter(null);
    setPage(0);
  };

  const tableCaixas = useMemo(() => {
    if (!cardFilter) return caixas;
    return summaryCaixas.filter((caixa) => matchesCardFilter(caixa, cardFilter));
  }, [cardFilter, summaryCaixas, caixas]);

  const sortedCaixas = useMemo(() => {
    const sorted = [...tableCaixas];
    if (!orderBy) return sorted;

    sorted.sort((a, b) => {
      const valueA = orderBy === 'status' ? getCaixaStatusSortRank(a) : getPortasLivres(a);
      const valueB = orderBy === 'status' ? getCaixaStatusSortRank(b) : getPortasLivres(b);
      const cmp = valueA - valueB;
      return order === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [tableCaixas, orderBy, order]);

  const paginatedCaixas = useMemo(() => {
    if (!cardFilter) return sortedCaixas;
    const start = page * rowsPerPage;
    return sortedCaixas.slice(start, start + rowsPerPage);
  }, [cardFilter, sortedCaixas, page, rowsPerPage]);

  const displayTotalCount = cardFilter ? tableCaixas.length : totalCount;
  const isTableLoading = cardFilter ? summaryLoading : loading;

  // Seleção derivada (depende de paginatedCaixas)
  const selectableCaixas = useMemo(() => {
    return paginatedCaixas.filter((c) => c.tipo === 'cto' && Number(c.portas_em_uso ?? 0) > 0);
  }, [paginatedCaixas]);

  const selectedCaixasList = useMemo(() => {
    return paginatedCaixas.filter((c) => selectedCaixaIds.has(c.id));
  }, [paginatedCaixas, selectedCaixaIds]);

  const handleSelectAllCaixas = (checked: boolean) => {
    if (checked) {
      setSelectedCaixaIds(new Set(selectableCaixas.map((c) => c.id)));
    } else {
      setSelectedCaixaIds(new Set());
    }
  };

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedCaixaIds.size);
    }
  }, [selectedCaixaIds, onSelectionChange]);

  useEffect(() => {
    if (notifyTrigger && notifyTrigger > 0 && selectedCaixaIds.size > 0) {
      handleOpenNotifyModal();
    }
  }, [notifyTrigger]);

  const isAllSelected = selectableCaixas.length > 0 && selectableCaixas.every((c) => selectedCaixaIds.has(c.id));
  const isSomeSelected = selectableCaixas.some((c) => selectedCaixaIds.has(c.id)) && !isAllSelected;


  const renderKpiCard = (filter: CardFilter, card: React.ReactNode) => (
    <Box
      onClick={() => handleCardFilterClick(filter)}
      sx={{
        height: '100%',
        cursor: 'pointer',
        borderRadius: 2,
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        boxShadow: cardFilter === filter ? `0 0 0 2px ${theme.palette.primary.main}` : 'none',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: cardFilter === filter
            ? `0 0 0 2px ${theme.palette.primary.main}`
            : `0 4px 12px ${alpha(theme.palette.common.black, 0.12)}`,
        },
      }}
    >
      {card}
    </Box>
  );

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid item xs={6} sm={4} md={2}>
          <SysKpiCard
            titulo="Total de Caixas"
            valor={summaryLoading ? '...' : summary.totalCaixas}
            subtitulo={
              summaryLoading
                ? undefined
                : formatTotalCaixasSubtitle(summary)
            }
            icone={<InfoIcon sx={{ color: theme.palette.info.main }} />}
            cor={theme.palette.info.main}
            bgCor={alpha(theme.palette.info.main, 0.12)}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          {renderKpiCard('online', (
            <SysKpiCard
              titulo="CTO Online"
              valor={summaryLoading ? '...' : summary.online}
              icone={<CheckCircleIcon sx={{ color: theme.palette.success.main }} />}
              cor={theme.palette.success.main}
              bgCor={alpha(theme.palette.success.main, 0.12)}
            />
          ))}
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          {renderKpiCard('offline', (
            <SysKpiCard
              titulo="CTO Offline"
              valor={summaryLoading ? '...' : summary.offline}
              icone={<ErrorIcon sx={{ color: theme.palette.error.main }} />}
              cor={theme.palette.error.main}
              bgCor={alpha(theme.palette.error.main, 0.12)}
            />
          ))}
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          {renderKpiCard('aguardando_instalacao', (
            <SysKpiCard
              titulo="Aguardando Instalação"
              valor={summaryLoading ? '...' : summary.aguardandoInstalacao}
              icone={<SettingsIcon sx={{ color: theme.palette.warning.main }} />}
              cor={theme.palette.warning.main}
              bgCor={alpha(theme.palette.warning.main, 0.12)}
            />
          ))}
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          {renderKpiCard('ativas', (
            <SysKpiCard
              titulo="CTO Ativas"
              valor={summaryLoading ? '...' : summary.ativas}
              icone={<CheckCircleIcon sx={{ color: theme.palette.primary.main }} />}
              cor={theme.palette.primary.main}
              bgCor={alpha(theme.palette.primary.main, 0.12)}
            />
          ))}
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          {renderKpiCard('zero_portas_livres', (
            <SysKpiCard
              titulo="0 Portas Livres (Cheia)"
              valor={summaryLoading ? '...' : summary.zeroPortasLivres}
              icone={<ReportProblemIcon sx={{ color: theme.palette.error.dark }} />}
              cor={theme.palette.error.dark}
              bgCor={alpha(theme.palette.error.main, 0.12)}
            />
          ))}
        </Grid>
      </Grid>

      <Paper sx={{ p: 1.5, mb: 1 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={tipoFilter}
                onChange={(e) => {
                  setCardFilter(null);
                  setTipoFilter(e.target.value as TipoFilter);
                }}
                label="Tipo"
              >
                <MenuItem value="all">CTO + CEO</MenuItem>
                <MenuItem value="cto">CTO</MenuItem>
                <MenuItem value="ceo">CEO</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3.5}>
            <EscritorioSelect
              value={escritorioFilter}
              onChange={(value) => {
                setCardFilter(null);
                setEscritorioFilter(value as number | null);
              }}
              fullWidth
              size="small"
              label="Escritório"
              placeholder="Todos os escritórios"
            />
          </Grid>

          <Grid item xs={12} sm={3} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setCardFilter(null);
                  setStatusFilter(e.target.value as StatusFilter);
                }}
                label="Status"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="ativo">Ativo</MenuItem>
                <MenuItem value="inativo">Inativo</MenuItem>
                <MenuItem value="aguardando_instalacao">Aguardando Instalação</MenuItem>
                <MenuItem value="manutencao">Manutenção</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Portas</InputLabel>
              <Select
                value={portsFilter}
                onChange={(e) => {
                  setCardFilter(null);
                  setPortsFilter(e.target.value);
                }}
                label="Portas"
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="8">8 Portas</MenuItem>
                <MenuItem value="16">16 Portas</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={9}>
            <TextField
              fullWidth
              label="Buscar"
              placeholder="Nome ou endereço"
              value={searchTerm}
              onChange={(e) => {
                setCardFilter(null);
                setSearchTerm(e.target.value);
              }}
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={12} md={3}>
            <Box display="flex" gap={1} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
                size="small"
                sx={{ fontSize: '0.8rem' }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {cardFilter && (
        <Alert
          severity="info"
          sx={{ mb: 1, py: 0.25 }}
          action={
            <Button color="inherit" size="small" onClick={clearCardFilter}>
              Limpar
            </Button>
          }
        >
          Exibindo caixas do card: <strong>{cardFilterLabels[cardFilter]}</strong> ({tableCaixas.length})
        </Alert>
      )}

      {/* Tabela */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader sx={{ '& .MuiTableBody-root .MuiTableCell-root': { py: 0.5, fontSize: '0.78rem' } }}>
            <TableHead>
              <TableRow sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (t) => `color-mix(in srgb, ${theme.palette.primary.main} 12%, ${t.palette.background.paper})`,
                  fontWeight: 700,
                  fontSize: '0.73rem',
                  py: 0.8,
                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              }}>
                <TableCell padding="checkbox" sx={{ width: 40 }}>
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={isSomeSelected}
                    onChange={(e) => handleSelectAllCaixas(e.target.checked)}
                    sx={{ p: 0.25 }}
                  />
                </TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Endereço</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell sortDirection={orderBy === 'status' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'portas' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'portas'}
                    direction={orderBy === 'portas' ? order : 'asc'}
                    onClick={() => handleRequestSort('portas')}
                  >
                    Portas
                  </TableSortLabel>
                </TableCell>
                <TableCell>Porta PON</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isTableLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedCaixas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Nenhuma CAIXA encontrada
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCaixas.map((caixa) => (
                  <TableRow key={caixa.id} hover sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}>
                    <TableCell padding="checkbox">
                      {caixa.tipo === 'cto' && Number(caixa.portas_em_uso ?? 0) > 0 ? (
                        <Checkbox
                          size="small"
                          checked={selectedCaixaIds.has(caixa.id)}
                          onChange={() => handleToggleSelectCaixa(caixa.id)}
                          sx={{ p: 0.25 }}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography onClick={() => handleViewDetails(caixa)} style={{ cursor: 'pointer' }} variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem', mb: 0.1 }}>
                          {caixa.name}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {caixa.address}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={caixa.tipo?.toUpperCase() || 'CTO'}
                        color={caixa.tipo === 'ceo' ? 'info' : 'primary'}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </TableCell>

                    <TableCell>
                      {caixa.status === 'ativo' ? (
                        <Box
                          component="span"
                          sx={getCaixaMonitorStatus(caixa) === 'offline' ? offlineChipBlinkSx : undefined}
                          display="inline-flex"
                        >
                          <MonitorStatusChip status={getCaixaMonitorStatus(caixa)} />
                        </Box>
                      ) : (
                        <Chip
                          icon={getCaixaStatusIcon(caixa.status)}
                          label={getCaixaStatusLabel(caixa.status)}
                          color={getCaixaStatusColor(caixa.status) as any}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.1 }}>
                          {formatPortasCaixa(caixa)}
                        </Typography>
                        {(() => {
                          const desabilitadas = getDisabledPortsCount(caixa);
                          return caixa.tipo === 'cto' && desabilitadas > 0 ? (
                            <Typography variant="caption" color="error" sx={{ fontSize: '0.7rem' }}>
                              {desabilitadas} desabilitada{desabilitadas !== 1 ? 's' : ''}
                            </Typography>
                          ) : null;
                        })()}
                      </Box>
                    </TableCell>

                    <TableCell>
                      {hasPonVinculo(caixa) ? (
                        <Box>
                          {caixa.pon_port?.olt?.name && (
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.1 }}>
                              {caixa.pon_port.olt.name}
                            </Typography>
                          )}
                          {getPonPortSfp(caixa) && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                              SFP: {getPonPortSfp(caixa)}
                            </Typography>
                          )}
                          {!caixa.pon_port?.olt?.name && !getPonPortSfp(caixa) && (
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              Vinculada
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          Não vinculada
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Box display="flex" gap={0.25}>
                        {caixa.tipo === 'cto' && (
                          <Tooltip title="Visualizar Detalhes">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleViewDetails(caixa)}
                              sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                            >
                              <InfoIcon />
                            </IconButton>
                          </Tooltip>
                        )}


                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenCaixaForm(caixa)}
                            sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={caixa.status === 'ativo' ? 'Desativar' : 'Ativar'}>
                          <IconButton
                            size="small"
                            color={caixa.status === 'ativo' ? 'warning' : 'success'}
                            onClick={() => handleToggleStatus(caixa)}
                            sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                          >
                            {caixa.status === 'ativo' ? <PauseIcon /> : <CheckCircleIcon />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {displayTotalCount > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 0.5,
              px: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <TablePagination
              rowsPerPageOptions={[50, 100, 200, 500, 1000]}
              component="div"
              count={displayTotalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                setPage(0);
              }}
              labelRowsPerPage="Itens por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{
                '.MuiTablePagination-toolbar': {
                  minHeight: 34,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 2,
                  '& .MuiTablePagination-spacer': { display: 'none' },
                },
                '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.875rem' },
                '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.875rem' },
                '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.875rem' },
                '.MuiTablePagination-actions': { ml: 0.5 },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Modal para Formulário CAIXA */}
      <Dialog
        open={caixaFormDialog.open}
        onClose={handleCloseCaixaForm}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 3 }}>
          {(caixaFormDialog.caixa?.tipo === 'ceo' || caixaFormDialog.tipo === 'ceo') ? (
            <FtthCaixaCeoForm
              caixa={caixaFormDialog.caixa || undefined}
              onSuccess={handleCaixaFormSuccess}
              onCancel={handleCloseCaixaForm}
            />
          ) : (
            <CaixaForm
              caixa={caixaFormDialog.caixa || undefined}
              onSuccess={handleCaixaFormSuccess}
              onCancel={handleCloseCaixaForm}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Notificação em Massa */}
      <Dialog
        open={notifyModalOpen}
        onClose={handleCloseNotifyModal}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={notifyLoading}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <WhatsAppIcon color="success" />
              <Typography variant="h6" fontWeight="bold">
                Notificar Clientes em Massa
              </Typography>
            </Box>
            <IconButton onClick={handleCloseNotifyModal} size="small" disabled={notifyLoading}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {selectedCaixasList.length} caixa(s) selecionada(s)
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {notifyStep === 'form' ? (
            <Box>
              {/* Lista de caixas selecionadas */}
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Caixas selecionadas:
              </Typography>
              <Box
                sx={{
                  maxHeight: 120,
                  overflowY: 'auto',
                  mb: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                }}
              >
                <List dense disablePadding>
                  {selectedCaixasList.map((caixa) => (
                    <ListItem key={caixa.id} sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <PersonIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={caixa.name}
                        secondary={`${caixa.portas_em_uso ?? 0} cliente(s) · ${caixa.address || ''}`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Gateway */}
              <Box sx={{ mb: 2 }}>
                <GatewayWhatsappSelect
                  value={notifyGateway}
                  onChange={setNotifyGateway}
                  label="Gateway WhatsApp"
                  required
                  disabled={notifyLoading}
                  size="small"
                  helperText="Selecione o gateway para envio das mensagens"
                />
              </Box>

              {/* Mensagem */}
              <Box>
                <SysEditorWhatsapp
                  value={notifyMensagem}
                  onChange={setNotifyMensagem}
                  placeholder="Digite a mensagem que será enviada para todos os clientes das caixas selecionadas..."
                  maxLength={1000}
                  disabled={notifyLoading}
                  showPreview={true}
                  rows={4}
                  label="Mensagem *"
                  helperText="Use formatação *negrito* e _itálico_ para destacar texto"
                />
              </Box>
            </Box>
          ) : notifyStep === 'sending' ? (
            /* Progresso */
            <Box sx={{ py: 3 }}>
              <Typography variant="body1" fontWeight={600} align="center" sx={{ mb: 2 }}>
                Processando caixas...
              </Typography>
              <LinearProgress
                variant="determinate"
                value={notifyProgress.total > 0 ? (notifyProgress.current / notifyProgress.total) * 100 : 0}
                sx={{ height: 8, borderRadius: 4, mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary" align="center">
                Caixa {notifyProgress.current} de {notifyProgress.total}
              </Typography>
              <Typography variant="body2" fontWeight={500} align="center" sx={{ mt: 0.5 }}>
                {notifyProgress.currentCaixaName}
              </Typography>
            </Box>
          ) : (
            /* Resultados */
            <Box>
              <Alert
                severity={notifyResults.every((r) => r.success) ? 'success' : notifyResults.some((r) => r.success) ? 'warning' : 'error'}
                sx={{ mb: 2 }}
              >
                {notifyResults.filter((r) => r.success && !r.error).length} mensagem(ns) cadastrada(s)
                {notifyResults.filter((r) => r.success && r.error).length > 0 &&
                  `, ${notifyResults.filter((r) => r.success && r.error).length} já notificado(s)`}
                {notifyResults.filter((r) => !r.success).length > 0 &&
                  `, ${notifyResults.filter((r) => !r.success).length} falha(s)`}
              </Alert>

              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                <List dense>
                  {notifyResults.map((result, index) => (
                    <ListItem key={index} sx={{ py: 0.15 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {result.success && !result.error ? (
                          <CheckCircleOutlineIcon fontSize="small" color="success" />
                        ) : result.success && result.error ? (
                          <CheckCircleOutlineIcon fontSize="small" color="info" />
                        ) : (
                          <ErrorOutlineIcon fontSize="small" color="error" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" component="span" fontWeight={500}>
                            <Typography variant="caption" component="span" color="text.secondary" sx={{ mr: 0.5 }}>
                              [{result.caixaName}]
                            </Typography>
                            {result.clientName}
                          </Typography>
                        }
                        secondary={result.success ? (result.error || 'Mensagem cadastrada') : result.error}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          color: result.success ? (result.error ? 'info.main' : 'success.main') : 'error.main'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {notifyStep === 'form' ? (
            <>
              <Button onClick={handleCloseNotifyModal} disabled={notifyLoading}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleSendBulkNotifications}
                disabled={notifyLoading || !notifyMensagem.trim() || !notifyGateway || selectedCaixasList.length === 0}
                startIcon={<NotificationsIcon />}
              >
                Cadastrar Mensagens
              </Button>
            </>
          ) : notifyStep === 'sending' ? (
            <Button disabled>
              Processando...
            </Button>
          ) : (
            <Button variant="contained" onClick={handleCloseNotifyModal}>
              Fechar
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CaixasTab; 